import { useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import {
  downscaleImage,
  formatFileSize,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
  type Attachment,
} from '../features/plans/attachmentApi'
import type { ItemKind } from '../features/plans/itemApi'

export function AttachmentControl({
  eventId,
  kind,
  itemId,
  required,
}: {
  eventId: string
  kind: ItemKind
  itemId: string
  required: boolean
}) {
  const { user } = useAuth()
  const { data: attachments } = useAttachments(eventId, kind, itemId)
  const upload = useUploadAttachment(eventId, kind, itemId)
  const remove = useDeleteAttachment(eventId, kind, itemId)

  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyWith, setBusyWith] = useState<string | null>(null)

  const canDelete = (attachment: Attachment) =>
    attachment.uploadedByUserId === user?.id || (user?.role ?? 'Member') !== 'Member'

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setError(null)

    for (const file of Array.from(files)) {
      setBusyWith(file.name)
      try {
        await upload.mutateAsync(await downscaleImage(file))
      } catch (err) {
        setError(err instanceof ApiError ? err.detail : `Could not upload ${file.name}.`)
        break
      } finally {
        setBusyWith(null)
      }
    }

    // Both are cleared: the same handler serves either, and a value left behind
    // stops the identical file being chosen twice in a row.
    if (fileInput.current) {
      fileInput.current.value = ''
    }

    if (cameraInput.current) {
      cameraInput.current.value = ''
    }
  }

  return (
    <div className="panel">
      <h3>
        Attachments
        {required ? <span className="badge"> Required</span> : null}
      </h3>

      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}

      {attachments && attachments.length > 0 ? (
        <ul className="note-list">
          {attachments.map((attachment) => (
            <li key={attachment.id}>
              <a href={attachment.downloadUrl} download>
                {attachment.fileName}
              </a>
              <span className="hint">
                {formatFileSize(attachment.sizeBytes)} · {attachment.uploadedByDisplayName} ·{' '}
                {new Date(attachment.uploadedAt).toLocaleDateString()}
              </span>
              {canDelete(attachment) ? (
                <button
                  type="button"
                  onClick={async () => {
                    setError(null)
                    try {
                      await remove.mutateAsync(attachment.id)
                    } catch (err) {
                      setError(err instanceof ApiError ? err.detail : 'Could not remove that file.')
                    }
                  }}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty">No files attached.</p>
      )}

      {/*
        Two inputs rather than one with `capture`. A single input carrying
        `capture="environment"` sends some mobile browsers straight to the
        camera with no way to reach the files already on the device - which is
        exactly the phone, onsite, where someone needs to attach a spreadsheet
        they were emailed. The camera keeps its own control; this one opens the
        picker, unfiltered, so anything the server accepts can be chosen and a
        refusal arrives as a sentence rather than as a greyed-out file.
      */}
      <input
        ref={fileInput}
        id={`attach-${itemId}`}
        type="file"
        multiple
        onChange={(event) => void handleFiles(event.target.files)}
      />
      <label htmlFor={`attach-${itemId}`} className="hint">
        Choose a file. Photographs are shrunk before upload; the limit is 25 MB. Programs and
        scripts are refused.
      </label>

      <input
        ref={cameraInput}
        id={`capture-${itemId}`}
        type="file"
        accept="image/*"
        capture="environment"
        className="visually-hidden"
        onChange={(event) => void handleFiles(event.target.files)}
      />
      <label htmlFor={`capture-${itemId}`} className="button">
        Take a photo
      </label>

      {busyWith ? <p className="hint">Uploading {busyWith}…</p> : null}
    </div>
  )
}
